import type { UserInfo } from "@/redux/types/customerTypes";
import {
    CHARGERS, POWER_GENERATION, FUEL, SOLAR, GENERAL, MAPS, CAMERAS,
    ALARMS, LOGS, COMMUNICATION, STARLINK, EVENTS, REPORTS,
    HELP, MAINTENANCE, GLOBAL_REPORTS, POWER_DIAG, INTRODUCTION,
    UPCOMING_MAINTENANCE, GLOBAL_MAPS, DASHBOARD, MANUALS, REMOTE,
    GLOBAL_LOGS, SUPPORT, DOCUMENTATION, CONTROL_PANEL
} from './constants';

/* ── Case-insensitive component name normalization ── */
const COMPONENT_LOOKUP = new Map<string, string>();
[
    CHARGERS, POWER_GENERATION, FUEL, SOLAR, GENERAL, MAPS, CAMERAS,
    ALARMS, LOGS, COMMUNICATION, STARLINK, EVENTS, REPORTS,
    HELP, MAINTENANCE, GLOBAL_REPORTS, POWER_DIAG, INTRODUCTION,
    UPCOMING_MAINTENANCE, GLOBAL_MAPS, DASHBOARD, MANUALS, REMOTE,
    GLOBAL_LOGS, SUPPORT, DOCUMENTATION, CONTROL_PANEL
].forEach(c => COMPONENT_LOOKUP.set(c.toLowerCase(), c));
// Aliases
COMPONENT_LOOKUP.set('charger', CHARGERS);

/** Normalize a component name to match frontend constants (case-insensitive). */
export const normalizeComponentName = (name: string): string =>
    COMPONENT_LOOKUP.get(name.toLowerCase()) || name;

/* ── Case-insensitive preference key normalization ── */
const PREF_KEY_MAP = new Map<string, string>();
[
    'timezone', 'factory', 'dashSettings', 'maintenance', 'report',
    'remote', 'globalLogs', 'executive', 'parent', 'manuals', 'loki',
    'documentation', 'support', 'controlpanel'
].forEach(k => PREF_KEY_MAP.set(k.toLowerCase(), k));

/** Normalize preference object keys to match the PermittedCustomer.preferences interface (case-insensitive). */
export const normalizePreferenceKeys = (prefs: Record<string, any>): Record<string, any> => {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(prefs)) {
        const normalizedKey = PREF_KEY_MAP.get(key.toLowerCase()) || key;
        result[normalizedKey] = value;
    }
    return result;
};

/**
 * Checks if a user has permission to access a specific component for a given customer.
 * Permissions are customer-specific - same user may have different permissions for different customers.
 *
 * @param userInfo - The user information object from Redux state
 * @param componentName - The component name (e.g., "Cameras", "Power", "Maintenance")
 * @param selectedCustomer - The currently selected customer name
 * @returns boolean - true if user has permission, false otherwise
 */
export const hasUserPermission = (
    userInfo: UserInfo | null,
    componentName: string,
    selectedCustomer: string
): boolean => {
    // If no userInfo, deny access
    if (!userInfo) {
        return false;
    }

    // If component_access not available, allow access (backward compatibility)
    if (!userInfo.component_access) {
        return true;
    }

    // If no selected customer, deny access
    if (!selectedCustomer) {
        return false;
    }

    // Handle both formats: array (customer-level users) or object (admin users)
    let customerComponentAccess: string[] | null = null;

    if (Array.isArray(userInfo.component_access)) {
        // Customer-level user: component_access is a flat array
        customerComponentAccess = userInfo.component_access;
    } else {
        // Admin user: component_access is keyed by customer name
        customerComponentAccess = userInfo.component_access[selectedCustomer] || null;
    }

    // If no permissions defined, deny access
    if (!customerComponentAccess || !Array.isArray(customerComponentAccess)) {
        return false;
    }

    // Check if the component is in the user's access list (case-insensitive)
    return customerComponentAccess.some(
        (component) => component.toLowerCase() === componentName.toLowerCase()
    );
};
